/**
 * Document attribute Playwright (and other automation) can wait on once the
 * authenticated app has finished its blocking loaders for the current route.
 *
 * Set on `<html>` from {@link DataLoaderProxy} when children are actually rendered.
 */
export const NODON_APP_READY_ATTR = 'data-nodon-app-ready';

/**
 * Set on the Mapbox GL canvas (`role="region"` name Map) once attach has
 * finished and draft clicks are subscribed. Overlay copy can appear earlier.
 */
export const NODON_DESIGN_MAP_READY_ATTR = 'data-nodon-design-map-ready';

export type NodonAppReadyView = 'project' | 'project-list' | 'shared';

/**
 * Marks the document ready for the given view. Clears any previous value first.
 *
 * @param view - Route kind that finished loading
 */
export const setNodonAppReady = (view: NodonAppReadyView): void => {
  document.documentElement.setAttribute(NODON_APP_READY_ATTR, view);
};

/** Clears the readiness attribute (e.g. while blocking loaders are shown again). */
export const clearNodonAppReady = (): void => {
  document.documentElement.removeAttribute(NODON_APP_READY_ATTR);
};

const setReadyAttribute = (element: HTMLElement, ready: boolean): void => {
  if (ready) {
    element.setAttribute(NODON_DESIGN_MAP_READY_ATTR, 'true');
    return;
  }
  element.removeAttribute(NODON_DESIGN_MAP_READY_ATTR);
};

/**
 * Marks or clears Design map interactivity. Mapbox exposes `role="region"`
 * `aria-label="Map"` on the canvas, so Playwright waits on that node.
 *
 * @param container - Mapbox `container` div, or null while unmounted
 * @param ready - True after attach has bound draft handlers
 */
export const setNodonDesignMapReady = (
  container: HTMLElement | null,
  ready: boolean,
): void => {
  if (!container) return;
  setReadyAttribute(container, ready);
  const canvas = container.querySelector<HTMLElement>('canvas.mapboxgl-canvas');
  if (canvas) setReadyAttribute(canvas, ready);
};
